// Flexible multi-series chart. One component handles line / area / bar with
// optional stacking — driven by a `type` prop. Used by the pivotable P&L card.

import { Chart } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler)

export type ChartType = 'line' | 'bar' | 'stacked-area' | 'stacked-bar'

export interface TrendSeries {
  label: string
  data: number[]
  color: string
  dashed?: boolean
  fill?: boolean
}

interface Props {
  labels: string[]
  series: TrendSeries[]
  type: ChartType
  height?: number | string
  yTickFormat?: (v: number) => string
  showLegend?: boolean
}

export function TrendChart({ labels, series, type, height = 320, yTickFormat, showLegend }: Props) {
  const stacked = type === 'stacked-area' || type === 'stacked-bar'
  const isBar   = type === 'bar' || type === 'stacked-bar'
  const isArea  = type === 'stacked-area'

  // Build datasets per chart type
  const datasets = series.map((s) => {
    if (isBar) {
      return {
        type: 'bar' as const,
        label: s.label,
        data: s.data,
        backgroundColor: s.color,
        borderColor: s.color,
        borderRadius: 4,
        borderSkipped: false,
        maxBarThickness: 56,
        stack: stacked ? 'stack-1' : undefined,
      }
    }
    // line / area
    return {
      type: 'line' as const,
      label: s.label,
      data: s.data,
      borderColor: s.color,
      backgroundColor: isArea ? withAlpha(s.color, 0.6) : (s.fill ? withAlpha(s.color, 0.12) : 'transparent'),
      tension: 0.35,
      fill: isArea ? 'origin' : (s.fill ? true : false),
      borderWidth: isArea ? 1 : 2.5,
      pointRadius: isArea ? 0 : 3,
      pointBackgroundColor: s.color,
      borderDash: s.dashed ? [4, 4] : undefined,
    }
  })

  const legend = showLegend ?? (series.length > 1)

  return (
    <div style={{ height, width: '100%' }}>
      <Chart
        type={isBar ? 'bar' : 'line'}
        data={{ labels, datasets }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: stacked ? 'index' : 'nearest', intersect: false },
          plugins: {
            legend: legend
              ? { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, padding: 14, font: { size: 11 } } }
              : { display: false },
            tooltip: {
              callbacks: {
                label: (c) => {
                  const v = c.parsed.y ?? 0
                  return c.dataset.label + ': ' + (yTickFormat ? yTickFormat(v) : v.toLocaleString())
                },
              },
            },
          },
          scales: {
            y: {
              stacked,
              ticks: { callback: (v) => (yTickFormat ? yTickFormat(Number(v)) : String(v)), font: { family: 'JetBrains Mono' } },
              grid: { color: '#e2e8f0' },
            },
            x: {
              stacked,
              grid: { display: false },
              ticks: { font: { weight: 600 } },
            },
          },
        }}
      />
    </div>
  )
}

function withAlpha(hex: string, a: number) {
  const m = hex.match(/^#([0-9a-f]{6})$/i)
  if (!m) return hex
  const r = parseInt(m[1].slice(0, 2), 16), g = parseInt(m[1].slice(2, 4), 16), b = parseInt(m[1].slice(4, 6), 16)
  return `rgba(${r},${g},${b},${a})`
}
